import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useReferralAnalytics } from '../../../hooks/useAnalytics';
import MetricCard from '../core/MetricCard';
import AreaChart from '../../ui/AreaChart';
import BarChart from '../../ui/BarChart';
import DataTable from '../../ui/DataTable';
import CollapsibleSection from '../../ui/CollapsibleSection';
import { transformToChartData, transformToChartDataAsync } from '../../../services/chartService';
import { getPreviousPeriodValue } from '../../../utils/chartHelpers';
import { useFormatting } from '../../../hooks/useFormatting';
import { FILTER_PERIODS } from '../../../constants';

/**
 * Referral Metrics Component
 * Displays referral program analytics and leaderboard statistics
 * @param {Object} props - Component props
 * @param {string} props.period - Time period for metrics
 * @param {boolean} [props.compact] - Whether to display in compact mode
 */
const ReferralMetrics = ({ period, compact = false }) => {
  const { data: referralData, loading, error } = useReferralAnalytics(period);
  const { formatNumber } = useFormatting();

  // Generate chart data for key metrics using real data
  const [referralsChart, setReferralsChart] = useState(null);
  const [previousReferralData, setPreviousReferralData] = useState(null);

  // Fetch previous period data for accurate comparison
  useEffect(() => {
    const fetchPreviousPeriodData = async () => {
      try {
        const prevPeriodMap = {
          [FILTER_PERIODS.DAY]: FILTER_PERIODS.DAY,
          [FILTER_PERIODS.WEEK]: FILTER_PERIODS.WEEK,
          [FILTER_PERIODS.MONTH]: FILTER_PERIODS.MONTH,
          [FILTER_PERIODS.YEAR]: FILTER_PERIODS.YEAR,
          [FILTER_PERIODS.ALL]: FILTER_PERIODS.YEAR
        };
        
        const prevPeriod = prevPeriodMap[period] || FILTER_PERIODS.DAY;
        const { getReferralAnalytics } = await import('../../../services/firebaseService');
        const prevReferral = await getReferralAnalytics(prevPeriod).catch(() => null);
        setPreviousReferralData(prevReferral);
      } catch (error) {
        setPreviousReferralData(null);
      }
    };

    if (referralData) {
      fetchPreviousPeriodData();
    }
  }, [period, referralData]);

  useEffect(() => {
    if (!referralData) {
      setReferralsChart(null);
      return;
    }

    // Use actual previous period data if available, otherwise calculate from growth or use estimate
    const currentValue = referralData.totalReferralCount || 0;
    const previousValue = getPreviousPeriodValue(referralData, previousReferralData, 'totalReferralCount', 0.9);

    // Try to fetch real data from users collection
    transformToChartDataAsync(
      { totalReferralCount: currentValue },
      { totalReferralCount: previousValue },
      'totalReferralCount',
      period,
      {
        collectionName: 'users',
        valueExtractor: (docData) => docData.referralCount || 0
      }
    ).then(chart => {
      setReferralsChart(chart);
    }).catch(() => {
      // Fallback to synchronous generated data
      setReferralsChart(transformToChartData(
        { totalReferralCount: currentValue },
        { totalReferralCount: previousValue },
        'totalReferralCount',
        period
      ));
    });
  }, [referralData, previousReferralData, period]);

  // Prepare table data for leaderboard
  const tableColumns = [
    { key: 'rank', label: 'Rank' },
    { key: 'name', label: 'User' },
    { 
      key: 'points', 
      label: 'Points',
      render: (value) => typeof value === 'number' ? formatNumber(value) : value
    },
    { 
      key: 'referralCount', 
      label: 'Referrals',
      render: (value) => typeof value === 'number' ? formatNumber(value) : value
    }
  ];

  const tableData = useMemo(() => {
    if (!referralData?.mostActiveUsers) return [];
    return referralData.mostActiveUsers.map((user, index) => ({
      id: user.uid || index,
      rank: index + 1,
      name: user.name || user.email || 'Unknown',
      points: user.points || 0,
      referralCount: user.referralCount || 0
    }));
  }, [referralData]);

  if (error) {
    return (
      <div className="referral-metrics error">
        <h3>Error loading referral metrics</h3>
        <p>Please try refreshing the page</p>
      </div>
    );
  }

  const avgPointsPerUser = referralData?.mostActiveUsers?.length > 0 && referralData?.totalPointsDistributed
    ? (Number(referralData.totalPointsDistributed) / referralData.mostActiveUsers.length)
    : 0;

  return (
    <div className="referral-metrics">
      <h2 className="section-title"><i className="bi bi-trophy icon-brand" style={{ marginRight: 8 }}></i> Referral & Leaderboard Metrics</h2>
      
      {/* Key Metrics - Only Specified Ones Visible */}
      <div className="metrics-grid-wrapper">
        <div className="metrics-grid">
        <div className="metric-group">
          <h3 className="group-title">Referral & Leaderboard Metrics</h3>
          <div className="metric-cards">
            <MetricCard
              title="Total Referrals"
              value={formatNumber(referralData?.totalReferralCount || referralData?.totalReferrals || 0)}
              loading={loading}
              icon={<i className="bi bi-link-45deg icon-brand"></i>}
              color="blue"
              subtitle={`${period} period`}
            />
            <MetricCard
              title="Points Distributed"
              value={formatNumber(referralData?.totalPointsDistributed || 0)}
              loading={loading}
              icon={<i className="bi bi-star-fill icon-brand"></i>}
              color="green"
              subtitle="total points"
            />
          </div>
        </div>
        </div>
      </div>

      {/* Most Active Users Table - Always Visible */}
      {tableData.length > 0 && (
        <DataTable
          title="Most Active Users - Leaderboard"
          data={tableData}
          columns={tableColumns}
          loading={loading}
          searchable={true}
          sortable={true}
        />
      )}

      {/* All Other Metrics in Show More */}
      <CollapsibleSection
        title="Additional Referral Metrics"
        initialVisible={2}
        defaultExpanded={false}
      >
        {[
          <div key="additional" className="metric-group">
            <h3 className="group-title">Additional Metrics</h3>
            <div className="metric-cards">
              <MetricCard
                title="Most Active Users Count"
                value={formatNumber(referralData?.mostActiveUsers?.length || 0)}
                loading={loading}
                icon={<i className="bi bi-crown icon-brand"></i>}
                color="yellow"
                subtitle="top users"
              />
              <MetricCard
                title="Avg Points/User"
                value={typeof avgPointsPerUser === 'number' && !isNaN(avgPointsPerUser) ? formatNumber(Math.round(avgPointsPerUser)) : '0'}
                loading={loading}
                icon={<i className="bi bi-bar-chart icon-brand"></i>}
                color="purple"
                subtitle="per active user"
              />
            </div>
          </div>,

          // Charts in show more
          referralsChart && (
            <div key="referrals-chart" className={compact ? "chart-container-compact" : "chart-container"}>
              <AreaChart
                data={referralsChart.data}
                comparisonData={referralsChart.comparisonData}
                label="Total Referrals Over Time"
                formatType="number"
                color="green"
              />
            </div>
          ),

          tableData.length > 0 && (
            <div key="referrers-chart" className={compact ? "chart-container-compact" : "chart-container"}>
              <BarChart
                data={tableData.slice(0, 5).map(item => ({
                  label: item.name,
                  value: item.referralCount
                }))}
                label="Top 5 Referrers"
                formatType="number"
                orientation="horizontal"
              />
            </div>
          )
        ].filter(Boolean)}
      </CollapsibleSection>
    </div>
  );
};

ReferralMetrics.propTypes = {
  period: PropTypes.string.isRequired,
  compact: PropTypes.bool
};

ReferralMetrics.defaultProps = {
  compact: false
};

export default ReferralMetrics;
